import { Dialog, DialogBackdrop, DialogPanel, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { IoClose as CloseIcon } from 'react-icons/io5'

import { DatableLog, DatableTrace, Metadata, NodeError } from '@ui/interfaces'
import dayjs from '@ui/lib/utils/dayjs'

import { ViewErrorTimeseries } from './ViewErrorTimeseries'

interface ViewErrorModalProps {
  error: NodeError | null
  open: boolean
  setOpen: (value: boolean) => void
}

type NavButtonProps = {
  children: React.ReactNode
} & React.HTMLAttributes<HTMLButtonElement>

export const ViewErrorModal = ({ error, open, setOpen }: ViewErrorModalProps) => {
  const [errorRecordIndex, setErrorRecordIndex] = useState(0)

  if (!error) {
    return <Transition.Root show={open} as={'div'} />
  }

  let record: DatableLog | DatableTrace = {
    body: 'Could not find an input record associated with this error',
    timestamp: '',
    timestamp_nano: '',
    attributes: {},
  }
  let metadata: Metadata = { datatype: 'unknown' }
  const records = error.records

  if (records.length > 0) {
    record = records[errorRecordIndex].record
    metadata = records[errorRecordIndex].metadata
  }

  const navgiateToError = (direction: 'previous' | 'next') => {
    let index = errorRecordIndex
    if (direction === 'next') index++
    else if (direction === 'previous' && index === 0) index = records.length - 1
    else index--

    setErrorRecordIndex(index % records.length)
  }

  const NavButton = ({ children, ...props }: NavButtonProps) => {
    return (
      <button
        onClick={props.onClick}
        className="inline-flex w-20 justify-center rounded-md border border-red-600 bg-white px-1 py-1.5 text-sm font-medium text-red-600 transition duration-300 ease-in-out hover:bg-red-600 hover:text-white">
        {children}
      </button>
    )
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" static className="fixed inset-0 z-40 w-screen overflow-y-auto" open={open} onClose={() => {}}>
        <div className="fixed inset-0 flex min-h-screen w-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <Transition
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </Transition>

          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true"></span>

          <Transition
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel className="inline-block overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:p-6 sm:align-middle">
              <div className="flex-col sm:flex sm:items-start">
                <div className="flex w-full flex-row items-start justify-between">
                  <div className="flex flex-col">
                    <Dialog.Title as="h1" className="text-xl font-medium leading-6 text-gray-900">
                      {error.errorMessage}
                    </Dialog.Title>

                    <p className="text-sm text-gray-500"> {dayjs(error.lastOccurrence).format('MMM D HH:mm:ss:ms')}</p>
                  </div>

                  <CloseIcon
                    className="h-6 w-6 cursor-pointer rounded-full text-gray-900 hover:text-gray-500"
                    onClick={() => setOpen(false)}
                  />
                </div>

                <div className="mt-3 w-full text-center sm:text-left">
                  <ViewErrorTimeseries chartData={error.timeseries} />
                </div>

                <div className="mt-3 w-full text-center sm:text-left">
                  Datatype: <div className="inline font-semibold capitalize">{metadata.datatype}</div>
                  {records.length > 0 && (
                    <div className="flex-inline float-right">
                      Sample of input data [{errorRecordIndex + 1} / {records.length}]
                    </div>
                  )}
                </div>

                <div className="mt-3 h-96 w-full text-center sm:text-left">
                  <pre className="h-full overflow-y-scroll break-all rounded-sm bg-gray-100 p-2 text-sm text-gray-900">
                    {JSON.stringify(record, null, 2)}
                  </pre>
                </div>

                {records.length > 0 && (
                  <div className="mt-4 flex w-full flex-row justify-between">
                    <NavButton onClick={() => navgiateToError('previous')}>Previous</NavButton>
                    <NavButton onClick={() => navgiateToError('next')}>Next</NavButton>
                  </div>
                )}
              </div>
            </DialogPanel>
          </Transition>
        </div>
      </Dialog>
    </Transition>
  )
}
